import React, { Fragment, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
    FaVideo,
    FaPlay,
    FaCalendar,
    FaHdd,
    FaTimes,
    FaList,
    FaExclamationTriangle,
    FaClosedCaptioning,
    FaUser,
    FaSpinner,
    FaSync,
} from 'react-icons/fa';
import { Button, Modal } from 'react-bootstrap';
import dayjs from 'dayjs';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { fileApi } from '@/_services/api/file';
import { conferenceApi } from '@/_services/api/conference';
import OurAccordion from '@/_elements/OurAccordion';
import { toast } from 'react-toastify';

// Styled Components
const VideoThumbnailContainer = styled.div`
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid ${(props) => (props.isPlaying ? '#50aeb0' : '#ccc')};
    transition: all 0.2s ease;
    background: white;

    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
`;

const ThumbnailImage = styled.div`
    position: relative;
    background-color: #1f2937;
    aspect-ratio: 16/9;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const PlayButtonOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.4);
    opacity: 0;
    transition: opacity 0.2s ease;
`;

const PlayButton = styled.div`
    width: 48px;
    height: 48px;
    background-color: #50aeb0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const DurationBadge = styled.div`
    position: absolute;
    bottom: 8px;
    right: 8px;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 11px;
    padding: 3px 6px;
    border-radius: 4px;
`;

const PlayingIndicator = styled.div`
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: #50aeb0;
    color: white;
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const VideoInfo = styled.div`
    padding: 12px;
    background: white;
`;

const VideoTitle = styled.h4`
    font-weight: 500;
    font-size: 14px;
    margin: 0 0 8px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const VideoIndex = styled.span`
    font-size: 11px;
    color: #6b7280;
    background-color: #f3f4f6;
    padding: 2px 8px;
    border-radius: 12px;
`;

const VideoMetaContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
`;

const VideoMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: #6b7280;
`;

const RecordingsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    max-height: 300px;
    overflow-y: auto;
    padding: 4px;

    @media (min-width: 640px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const ErrorBadge = styled.span`
    background-color: #fee2e2;
    color: #dc2626;
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const RecordingsButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    background-color: #eff6ff;
    color: #1d4ed8;
    border: none;
    border-radius: 50%;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-left: 0px;

    /* This makes the button maintain a square aspect ratio */
    aspect-ratio: 1 / 1;

    /* Optional: Set minimum dimensions */
    min-width: 40px;
    min-height: 40px;

    &:hover {
        background-color: #dbeafe;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
`;

const LoadingSpinner = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #6b7280;
`;

const msToTimestamp = (ms) => {
    const date = new Date(ms);
    const h = String(date.getUTCHours()).padStart(2, '0');
    const m = String(date.getUTCMinutes()).padStart(2, '0');
    const s = String(date.getUTCSeconds()).padStart(2, '0');
    const msStr = String(ms % 1000).padStart(3, '0');
    return `${h}:${m}:${s}.${msStr}`;
};

const formatTranscript = (data) => {
    if (!data) {
        return {
            text: '',
            language: null,
            lines: [],
        };
    }

    let allSegments = [];
    if (Array.isArray(data.segments)) {
        allSegments = data.segments;
    } else if (Array.isArray(data.chunks)) {
        allSegments = data.chunks.flatMap((chunk) => chunk.segments || []);
    } else if (Array.isArray(data) && data.length > 0 && data[0].text !== undefined) {
        allSegments = data;
    }

    if (allSegments.length === 0) {
        return {
            text: data.text || '',
            language: data?.language,
            lines: [],
        };
    }

    const lines = allSegments.map((segment) => ({
        contents: segment.text?.trim() || '',
        startTime: msToTimestamp(segment.start * 1000),
        endTime: msToTimestamp(segment.end * 1000),
        startTimeMs: Math.floor(segment.start * 1000),
        endTimeMs: Math.floor(segment.end * 1000),
        speaker: segment.speaker || 'Unknown',
        speakerLabel: `Speaker ${segment.speaker || 'Unknown'}`,
        id: segment.id || `seg_${Math.random().toString(36).substr(2, 9)}`,
    }));

    const combinedText = data.text || allSegments.map((seg) => seg.text).join(' ');

    return {
        lines,
        text: combinedText,
        language: data?.language,
    };
};
const VideoThumbnail = ({ recording, index, isPlaying, onClick }) => {
    const formatSize = (bytes) => {
        if (!bytes) return '0 MB';
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const formatDuration = (duration) => {
        if (!duration) return '0:00';
        if (!isNaN(duration)) {
            const minutes = Math.floor(duration / 60);
            const seconds = Math.floor(duration % 60);
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
        return duration;
    };

    const [isHovered, setIsHovered] = useState(false);

    return (
        <VideoThumbnailContainer
            isPlaying={isPlaying}
            onClick={() => onClick(recording)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <ThumbnailImage>
                <div style={{ textAlign: 'center', color: 'white' }}>
                    <FaVideo style={{ fontSize: '24px', opacity: 0.8, marginBottom: '8px' }} />
                    <div style={{ color: '#d1d5db', fontSize: '12px' }}>Recording {index + 1}</div>
                </div>

                <PlayButtonOverlay style={{ opacity: isHovered ? 1 : 0 }}>
                    <PlayButton>
                        <FaPlay style={{ color: 'white', fontSize: '16px', marginLeft: '2px' }} />
                    </PlayButton>
                </PlayButtonOverlay>

                {recording.duration && <DurationBadge>{formatDuration(recording.duration)}</DurationBadge>}

                {isPlaying && (
                    <PlayingIndicator>
                        <FaPlay size={8} />
                        Playing
                    </PlayingIndicator>
                )}
            </ThumbnailImage>

            <VideoInfo>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <VideoTitle>{recording.title || `Recording ${index + 1}`}</VideoTitle>
                    <VideoIndex>#{index + 1}</VideoIndex>
                </div>

                <VideoMetaContainer>
                    <VideoMeta>
                        <FaHdd size={12} style={{ color: '#9ca3af' }} />
                        <span>{formatSize(recording.size)}</span>
                    </VideoMeta>

                    {recording.createdAt && (
                        <VideoMeta>
                            <FaCalendar size={12} style={{ color: '#9ca3af' }} />
                            <span>{dayjs(recording.createdAt).format('MMM D, YYYY')}</span>
                        </VideoMeta>
                    )}
                </VideoMetaContainer>
            </VideoInfo>
        </VideoThumbnailContainer>
    );
};

// Separate Transcript Component with speaker diarization
const TranscriptWithDiarization = React.memo(({ transcriptData, videoRef, isPlaying, setIsPlaying, formatTime }) => {
    const [currentLineIndex, setCurrentLineIndex] = useState(-1);
    const [autoScrollTranscript, setAutoScrollTranscript] = useState(true);
    const transcriptContainerRef = useRef(null);

    // Get speaker color for consistent highlighting
    const getSpeakerColor = (speaker = '') => {
        const speakerColors = {
            A: '#4f46e5', // indigo
            B: '#10b981', // emerald
            C: '#f59e0b', // amber
            D: '#ef4444', // red
            E: '#8b5cf6', // violet
            F: '#ec4899', // pink
            G: '#06b6d4', // cyan
            H: '#84cc16', // lime
            I: '#f97316', // orange
            J: '#0ea5e9', // sky
            K: '#14b8a6', // teal
            L: '#a855f7', // purple
        };

        return speakerColors[speaker.slice(0, 1)] || '#6b7280';
    };

    // Handle clicking on a transcript line
    const handleLineClick = (line) => {
        if (!videoRef.current) return;

        const seekTime = line.startTimeMs / 1000;
        videoRef.current.currentTime = seekTime;

        if (!isPlaying) {
            videoRef.current.play();
            setIsPlaying(true);
        }
    };

    // Sync transcript with video playback
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !transcriptData?.lines) return;

        const onTimeUpdate = () => {
            const currentTimeMs = video.currentTime * 1000;
            const idx = transcriptData.lines.findIndex(
                (line) => currentTimeMs >= line.startTimeMs && currentTimeMs <= line.endTimeMs
            );
            setCurrentLineIndex(idx);
            if (!autoScrollTranscript || idx === -1) return;

            // Auto-scroll to current line
            if (transcriptContainerRef.current) {
                const activeLine = transcriptContainerRef.current.children[idx];
                if (activeLine) {
                    activeLine.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'nearest',
                    });
                }
            }
        };

        video.addEventListener('timeupdate', onTimeUpdate);
        return () => video.removeEventListener('timeupdate', onTimeUpdate);
    }, [transcriptData?.lines, videoRef, autoScrollTranscript]);

    if (!transcriptData?.lines || transcriptData.lines.length === 0) {
        return (
            <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                <FaClosedCaptioning size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p>No transcript available</p>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="d-flex justify-content-end">
                <Button
                    variant="outline-secondary"
                    className="mb-1"
                    style={{ fontSize: '12px' }}
                    type="button"
                    onClick={() => setAutoScrollTranscript((prev) => !prev)}
                >
                    Auto Scroll: {autoScrollTranscript ? 'ON' : 'OFF'}
                </Button>
            </div>

            <div
                ref={transcriptContainerRef}
                style={{
                    maxHeight: '600px',
                    height: '200px',
                    minHeight: '120px',
                    overflowY: 'auto',
                    resize: 'vertical',
                    overscrollBehavior: 'contain',
                    border: '1px solid #e5e7eb',
                    padding: '8px',
                }}
            >
                {transcriptData.lines.map((line, idx) => {
                    const isActive = idx === currentLineIndex;
                    const speakerColor = getSpeakerColor(line.speaker);

                    return (
                        <div
                            key={`line-${idx}`}
                            onClick={() => handleLineClick(line)}
                            className={`transcript-line ${isActive ? 'active-line' : ''}`}
                            style={{
                                marginBottom: '4px',
                                padding: '6px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: isActive ? '#f9fafb' : 'transparent',
                                borderLeft: `4px solid ${isActive ? speakerColor : 'transparent'}`,
                                transition: 'all 0.2s ease',
                                border: isActive ? '1px solid #e5e7eb' : '1px solid transparent',
                            }}
                        >
                            <div className="d-flex align-items-start gap-2">
                                <div
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        backgroundColor: speakerColor + '20',
                                        color: speakerColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        marginTop: '2px',
                                    }}
                                >
                                    <FaUser size={12} />
                                </div>

                                <div style={{ flex: 1 }}>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <span
                                            style={{
                                                fontWeight: '600',
                                                fontSize: '14px',
                                                color: speakerColor,
                                            }}
                                            className="ml-2"
                                        >
                                            {line.speakerLabel}
                                        </span>
                                        <span className="text-muted ml-2" style={{ fontSize: '12px' }}>
                                            {formatTime(line.startTimeMs / 1000)}
                                        </span>
                                        {isActive && (
                                            <span
                                                style={{
                                                    fontSize: '10px',
                                                    backgroundColor: speakerColor,
                                                    color: 'white',
                                                    padding: '2px 6px',
                                                    borderRadius: '10px',
                                                    marginLeft: 'auto',
                                                }}
                                            >
                                                Playing
                                            </span>
                                        )}
                                    </div>

                                    <p
                                        style={{
                                            margin: 0,
                                            fontSize: '14px',
                                            lineHeight: '1.5',
                                            color: isActive ? '#111827' : '#374151',
                                        }}
                                    >
                                        {line.contents}
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Fragment>
    );
});

const VideoPlayerWithTranscript = ({ recording, conferenceData }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [transcriptData, setTranscriptData] = useState(null);
    const [loadingTranscript, setLoadingTranscript] = useState(false);
    const [transcriptError, setTranscriptError] = useState(null);
    const [text, setText] = useState(null);

    const fetchTranscript = async (url) => {
        if (!url) return;

        setLoadingTranscript(true);
        setTranscriptError(null);

        try {
            const response = await fileApi.getOrCreateTranscription(
                { url },
                `?conference=${conferenceData?._id || conferenceData?.id}`
            );
            if (response && response.data) {
                const formattedTranscript = formatTranscript(response.data);
                setTranscriptData(formattedTranscript);
            } else {
                setTranscriptData(null);
            }
            setText(response?.text || response?.data?.text || '-');
        } catch (error) {
            console.error('Error fetching transcript:', error);
            setTranscriptError(error?.message || 'Failed to load transcript');
            setTranscriptData(null);
        } finally {
            setLoadingTranscript(false);
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <div>
            <div style={{ marginBottom: '16px' }}>
                <video
                    ref={videoRef}
                    key={recording.url}
                    controls
                    autoPlay={false}
                    style={{
                        width: '100%',
                        maxHeight: '300px',
                        minHeight: '50px',
                        borderRadius: '8px',
                    }}
                    onError={(e) => {
                        console.error('Video loading error:', e);
                    }}
                >
                    <source src={recording.url} type="video/mp4" />
                    <source src={recording.url} type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            </div>

            <OurAccordion
                defaultShow={false}
                onToggle={() => fetchTranscript(recording?.url)}
                title={
                    <h3 style={{ fontWeight: '600', margin: 0 }}>
                        <FaClosedCaptioning className="mr-2" />
                        Transcript with Speaker Diarization
                    </h3>
                }
            >
                <div>
                    {loadingTranscript && (
                        <LoadingSpinner>
                            <FaSpinner className="fa-spin mr-2" />
                            Loading transcript...
                        </LoadingSpinner>
                    )}

                    {transcriptError && (
                        <div
                            style={{
                                padding: '16px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                textAlign: 'center',
                                color: '#dc2626',
                            }}
                        >
                            <FaExclamationTriangle className="mr-2" />
                            {transcriptError}
                        </div>
                    )}

                    {!loadingTranscript && !transcriptError && transcriptData && (
                        <TranscriptWithDiarization
                            transcriptData={transcriptData}
                            videoRef={videoRef}
                            isPlaying={isPlaying}
                            setIsPlaying={setIsPlaying}
                            formatTime={formatTime}
                        />
                    )}
                    <OurAccordion title={<strong>Show Full Text</strong>} defaultShow={false}>
                        <p>{text}</p>
                    </OurAccordion>

                    {!loadingTranscript && !transcriptError && !transcriptData && (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                            <FaClosedCaptioning size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                            <p>No transcript available for this recording</p>
                            <small>Transcripts may be generated after recording processing</small>
                        </div>
                    )}
                </div>
            </OurAccordion>
        </div>
    );
};

const RecordingsModal = ({ show, onHide, recordings = [], recordingStatus, conferenceData }) => {
    const [selectedRecording, setSelectedRecording] = useState(recordings?.[0] || null);

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Body style={{ padding: '16px' }}>
                {selectedRecording ? (
                    <div style={{ marginBottom: '12px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px',
                            }}
                        >
                            {selectedRecording.status === 'complete' && (
                                <span
                                    style={{
                                        fontSize: '12px',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '12px',
                                    }}
                                >
                                    Transcript Available
                                </span>
                            )}
                        </div>

                        <VideoPlayerWithTranscript
                            recording={selectedRecording}
                            onClose={onHide}
                            showFullTranscript={true}
                            conferenceData={conferenceData}
                        />
                    </div>
                ) : (
                    recordingStatus === 'failed' && (
                        <div
                            style={{
                                marginBottom: '16px',
                                padding: '16px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                            }}
                        >
                            <div
                                style={{ display: 'flex', alignItems: 'center', color: '#dc2626', marginBottom: '4px' }}
                            >
                                <FaTimes style={{ marginRight: '8px' }} />
                                <span style={{ fontWeight: 500 }}>Recording Failed</span>
                            </div>
                        </div>
                    )
                )}
                <OurAccordion
                    defaultShow={false}
                    title={
                        <h3 style={{ fontWeight: '600', margin: 0 }}>
                            <FaList style={{ color: '#111' }} />
                            <span className="ml-2">All Recordings</span>
                            <small style={{ color: '#6b7280' }} className="ml-2">
                                ({recordings.length})
                            </small>
                        </h3>
                    }
                >
                    <div style={{ marginTop: '24px' }}>
                        {recordings.length > 0 ? (
                            <RecordingsGrid>
                                {recordings.map((recording, index) => (
                                    <VideoThumbnail
                                        key={`recording-${index}`}
                                        recording={recording}
                                        index={index}
                                        isPlaying={selectedRecording?.url === recording.url}
                                        onClick={() => setSelectedRecording(recording)}
                                    />
                                ))}
                            </RecordingsGrid>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                                <FaVideo style={{ fontSize: '48px', opacity: 0.5, marginBottom: '12px' }} />
                                <p style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>
                                    No recordings available
                                </p>
                                <p style={{ fontSize: '14px' }}>
                                    Recordings will appear here once the conference ends.
                                </p>
                            </div>
                        )}
                    </div>
                </OurAccordion>
            </Modal.Body>
        </Modal>
    );
};

const Component = ({ item = {}, type = '', onUpdateItem = () => {} }) => {
    const [showRecordingsModal, setShowRecordingsModal] = useState(false);
    const hasRecordings = item.recordings && item.recordings.length > 0;
    const recordingStatus = item.extraInfo?.recordingStatus;
    const [retrying, setRetrying] = useState(false);

    const handleRetry = async (e) => {
        if (e) e.stopPropagation();
        setRetrying(true);
        try {
            const response = await conferenceApi.retryRecording(item.id || item?._id);
            onUpdateItem(response);
        } catch (error) {
            toast.error(error?.message || 'Retry failed');
            console.error('Retry failed:', error);
        } finally {
            setRetrying(false);
        }
    };

    if (type === 'message' && !hasRecordings) {
        return null;
    }
    if (recordingStatus) {
        return <span>{recordingStatus}</span>;
    }

    if (!hasRecordings) {
        const recordingCompositionStatus = item?.recordingCompositionStatus;
        const badgeStatus = recordingCompositionStatus === 'failed' ? 'badge-danger' : 'badge-info';
        const uploadRecordingError = (item?.extraInfo || {})?.uploadRecordingError;

        if (recordingCompositionStatus === 'failed' || uploadRecordingError) {
            return (
                <div className="d-flex flex-column align-items-center">
                    <span className={`badge ${badgeStatus}`}>{item?.recordingCompositionStatus}</span>{' '}
                    <Button
                        className="mt-1 px-2 py-0"
                        variant="outline-secondary"
                        onClick={handleRetry}
                        disabled={retrying}
                    >
                        <FaSync className={`mr-1 ${retrying ? 'spin' : ''}`} size={10} />
                        {retrying ? 'Retrying...' : 'Retry'}
                    </Button>
                </div>
            );
        }
        return (
            <span className="text-center w-full" style={{ width: '' }}>
                -
            </span>
        );
    }

    return (
        <Fragment>
            {showRecordingsModal && (
                <RecordingsModal
                    show={showRecordingsModal}
                    onHide={() => setShowRecordingsModal(false)}
                    recordings={item.recordings}
                    recordingStatus={recordingStatus}
                    conferenceData={item}
                />
            )}
            <RecordingsButton onClick={() => setShowRecordingsModal(true)} title="View Recordings">
                <FaVideo style={{ color: '#50aeb0' }} size={20} />
                {recordingStatus === 'failed' && (
                    <ErrorBadge style={{ marginLeft: '8px' }}>
                        <FaExclamationTriangle size={10} />
                        Error
                    </ErrorBadge>
                )}
            </RecordingsButton>
        </Fragment>
    );
};

const ConferenceRecordings = (props) => (
    <ComponentErrorBoundary componentErrorMessage={'Failed to show recordings'}>
        <Component {...props} />
    </ComponentErrorBoundary>
);
export default ConferenceRecordings;
